/**
 * InlineUpload — a document slot, one row high.
 *
 * THE ROW IS THE DROP TARGET. `FileUpload` is a 6-unit dashed box with a centred icon; five of
 * them stacked is a page of boxes and no document. A slot here is the same height as every other
 * row in the block — an explicit name on the left, `filename · size` and an ALWAYS-VISIBLE `Open`
 * on the right — so a driver file reads as a list of what is on file rather than as five upload
 * widgets, which is what it is.
 *
 * EXPLICITLY NAMED SLOTS, NEVER A PILE. `Ghana Card (front)` and `Ghana Card (back)` are two rows
 * with two names, not one control that takes two files: a pile cannot say which side is missing,
 * and "2 files" is not an answer to "did we photograph the back".
 *
 * THE OPEN CONTROL IS NOT HOVER-REVEALED. Every affordance on this surface is drawn at rest; a
 * control that appears under a pointer does not exist on a touch screen, and this row's whole job
 * is letting somebody look at the document.
 *
 * NOTHING HERE WRITES. A chosen file is STAGED — the row says so — and the group's commit bar
 * sends it with the rest of the block, like every other value on the canvas.
 */

import { ExternalLink, Paperclip, X } from 'lucide-react';
import { useRef, useState, type DragEvent, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { openSignedUrl } from '@/lib/openSignedUrl';
import { cn } from '@/lib/utils';

import { fileNameFromUrl, oversizeMessage, slotMeta } from './fileMeta';

interface Props {
    /** The DOM id a manifest row's `jump.focus` names — it lands on the choose control. */
    id: string;
    /** The slot's own name: `Ghana Card (front)`, `Roadworthiness certificate`. */
    label: string;
    /** What is already on file. */
    current?: { url: string | null; name?: string | null; size?: number | null } | null;
    /** Staged locally, awaiting this group's commit. */
    file: File | null;
    onFileChange: (file: File | null) => void;
    accept?: string;
    maxSizeMB?: number;
    /** The server's own refusal for this slot, verbatim. */
    error?: string | null;
    /**
     * What the slot learned from the file staged in it — the cover period read out of a
     * certificate, or the sentence saying it could not be. 12px muted, under the row. A ReactNode
     * so it can carry a spinner, and a link back, while a read is in flight.
     *
     * IT IS NOT A LIVE REGION AND MUST NOT BECOME ONE. `NarratorLine` owns the setup canvas's one
     * polite region (`StatsLedger.tsx:33`, `Manifest.tsx:32`), and a second one down here would
     * talk over the sentence that actually changed. The note is wired instead into the
     * `aria-describedby` of the button the admin just pressed — Choose a file, Replace, Remove —
     * so it is the description of that control: read out when focus is on it, and reachable from
     * it afterwards. Nothing here announces itself; what it buys is that the sentence is attached
     * to the control it is about rather than left as a paragraph below two other ones.
     */
    note?: ReactNode;
    /** What an empty slot says. Records are never urgent. */
    empty?: string;
    /** On file but expired — the one case a record row is allowed to go destructive. */
    bad?: boolean;
    disabled?: boolean;
}

export default function InlineUpload({
    id,
    label,
    current,
    file,
    onFileChange,
    accept = '.pdf,.jpg,.jpeg,.png',
    maxSizeMB = 5,
    error,
    note,
    empty = 'Not on file',
    bad,
    disabled,
}: Props) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [over, setOver] = useState(false);
    // A local refusal reads in the same slot a server one does, and clears the same way.
    const [refused, setRefused] = useState<string | null>(null);

    const storedName = current?.name?.trim() || fileNameFromUrl(current?.url);
    const onFile = !!current?.url || !!storedName;

    function take(chosen: File | null) {
        setRefused(null);
        if (!chosen) {
            onFileChange(null);
            return;
        }
        const tooBig = oversizeMessage(chosen, maxSizeMB);
        if (tooBig) {
            setRefused(tooBig);
            return;
        }
        onFileChange(chosen);
    }

    function drop(event: DragEvent<HTMLDivElement>) {
        event.preventDefault();
        setOver(false);
        if (disabled) return;
        take(event.dataTransfer.files?.[0] ?? null);
    }

    const message = error ?? refused;

    return (
        <div
            className={cn(
                'sd-up',
                !onFile && !file && 'sd-up-absent',
                bad && 'sd-up-bad',
                file && 'sd-up-staged',
                over && 'sd-up-over',
                message && 'sd-up-errored',
            )}
            onDragOver={(event) => {
                event.preventDefault();
                if (!disabled) setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={drop}
        >
            <div className="min-w-0">
                <div className="sd-up-n">{label}</div>
                <div className="sd-up-m">
                    {file ? (
                        <>
                            {slotMeta(file.name, file.size)}
                            <span className="sd-up-staged-note"> · saves with this block</span>
                        </>
                    ) : (
                        slotMeta(storedName, current?.size, empty)
                    )}
                </div>
            </div>

            <div className="sd-up-actions">
                {/* Always drawn, never hovered into existence. */}
                {onFile && !file && current?.url && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="sd-up-btn"
                        onClick={() => openSignedUrl(current.url)}
                    >
                        <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                        Open
                    </Button>
                )}
                {/* `id` RIDES THE VISIBLE CONTROL, not the file input: a manifest refusal that
                    routes here has to land the caret somewhere an admin can see it, and the input
                    itself is `sr-only`. */}
                {file ? (
                    <Button
                        id={id}
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="sd-up-btn"
                        onClick={() => take(null)}
                        disabled={disabled}
                        aria-label={`Remove ${file.name}`}
                        // Same flag every other primitive on the canvas raises from `error`, so a
                        // refused group can find this slot and put the caret on it.
                        aria-invalid={message ? true : undefined}
                        aria-describedby={
                            [message ? `${id}-err` : null, note ? `${id}-note` : null]
                                .filter(Boolean)
                                .join(' ') || undefined
                        }
                    >
                        <X className="mr-1.5 h-3.5 w-3.5" />
                        Remove
                    </Button>
                ) : (
                    <Button
                        id={id}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="sd-up-btn"
                        onClick={() => inputRef.current?.click()}
                        disabled={disabled}
                        aria-invalid={message ? true : undefined}
                        aria-describedby={
                            [message ? `${id}-err` : null, note ? `${id}-note` : null]
                                .filter(Boolean)
                                .join(' ') || undefined
                        }
                    >
                        <Paperclip className="mr-1.5 h-3.5 w-3.5" />
                        {onFile ? 'Replace' : 'Choose a file'}
                    </Button>
                )}
            </div>

            <input
                ref={inputRef}
                id={`${id}-input`}
                type="file"
                accept={accept}
                className="sr-only"
                tabIndex={-1}
                disabled={disabled}
                onChange={(event) => {
                    take(event.target.files?.[0] ?? null);
                    // So the same file can be chosen again after a refusal.
                    if (inputRef.current) inputRef.current.value = '';
                }}
            />

            {message && (
                <p className="sd-f-err sd-up-err" id={`${id}-err`}>
                    {message}
                </p>
            )}
            {/* NOT A REFUSAL, SO IT NEVER RAISES `aria-invalid`. `InlineFieldGroup` puts the
                caret on the first `[aria-invalid="true"]` after a refused commit; a note that
                flagged itself would steal it from the field the server actually refused. */}
            {note && (
                <p className="sd-up-note" id={`${id}-note`}>
                    {note}
                </p>
            )}
            {/* What the slot takes is worth saying while it is empty and worth nothing once it is
                full — the row's job then is the document, not the rules. */}
            {!onFile && !file && (
                <p className="sd-up-limit">
                    Drop a file here · {accept.replace(/\./g, '').split(',').join(', ')} · up to{' '}
                    {maxSizeMB} MB
                </p>
            )}
        </div>
    );
}
